import { utils, writeFile, writeFileXLSX, AOA2SheetOpts, type WorkBook } from 'xlsx';
import { getDateForFileExport, isRealYmd, todayInRome } from './get-date-worlds-format';

export const excelDownloader = ({
  fileName,
  fileType = 'xlsx',
  headers = [],
  rows = [],
}: {
  fileName: string;
  fileType: string;
  headers: string[];
  rows: (string | number)[][];
}) => {
  const wb = utils.book_new();
  // creating sheet and adding data from 2nd row of column A.
  // Leaving first row to add Heading
  const ws = utils.aoa_to_sheet(
    rows as unknown[][],
    { origin: 'A2', skipHeader: true } as AOA2SheetOpts,
  );

  // adding heading to the first row of the created sheet.
  // sheet already have contents from above statement.
  utils.sheet_add_aoa(ws, [headers], { origin: 'A1' });

  // Calculate the column widths dynamically based on the longest content in each column
  const colWidths = headers.map((header, colIdx) => {
    const headerLength = header.length;
    const maxCellLength = Math.max(
      ...rows.map((row) => (row[colIdx] ? String(row[colIdx]).length : 0)),
      headerLength,
    );
    // Set a minimum width to ensure that columns are not too narrow
    return { wch: Math.max(maxCellLength + 2, 10) }; // 'wch' is the width of the column in characters
  });

  // Set the column widths
  ws['!cols'] = colWidths;

  // Ensure that the sheet name does not exceed 31 characters
  const safeSheetName = fileName.substring(0, 31).replace(/[/:?*[\]]/g, '_');
  utils.book_append_sheet(wb, ws, safeSheetName);

  // Ensure that the file name does not exceed 250 characters. Max limit 255 char including file extension
  const safeFileName = fileName.substring(0, 250).replace(/[/:?*[\]]/g, '_');

  // Append date in dd-mm-yyyy format to filename
  const dateStr = getDateForFileExport();
  const fileNameWithDate = `${safeFileName} ${dateStr}`;

  writeFile(wb, `${fileNameWithDate}.${fileType}`);
};

// ----------------------------------------------------------------------
// NAT-2481 — typed exports for the BI stats tables. `excelDownloader` above writes every cell as
// the screen's formatted string; these write numbers as numbers and dates as dates, each with a
// number format, so the file can be summed, filtered and charted. Same library, same file-name
// convention (the date appended as dd-MM-yyyy).

export type XlsxCellKind =
  | 'text'
  | 'integer'
  | 'decimal'
  | 'decimal2' // two decimals, for a small ratio such as a velocity
  | 'euro'
  | 'percent' // a fraction: 0.125 shows as 12.5%
  | 'points'
  | 'date'
  | 'month'; // the first day of the month

export type XlsxValue = string | number | Date | null | undefined;
export type XlsxColumn = { header: string; kind: XlsxCellKind };
export type XlsxSheetSpec = { name: string; columns: XlsxColumn[]; rows: XlsxValue[][] };
export type XlsxWorkbookSpec = {
  /** Without extension or date: both are appended. */
  fileName: string;
  /** Label, value — what was exported, the period, every filter. Written above each sheet. */
  preamble: [string, string][];
  sheets: XlsxSheetSpec[];
};

/** More rows than this and the export refuses, asking for narrower filters. */
export const MAX_EXPORT_ROWS = 50_000;

/** Locale-neutral format codes: Excel shows the reader's own separators. */
export const XLSX_FORMATS: Record<XlsxCellKind, string | undefined> = {
  text: undefined,
  integer: '#,##0',
  decimal: '#,##0.0',
  decimal2: '#,##0.00',
  euro: '"€" #,##0.00',
  percent: '0.0%',
  points: '0.0 "pt"',
  date: 'dd/mm/yyyy',
  month: 'mmm yyyy',
};

/**
 * A `yyyy-MM-dd` (or `yyyy-MM`) string as a local-midnight Date: SheetJS turns a Date into an
 * Excel serial by its local fields, so a UTC midnight would land on the previous day west of UTC.
 */
export const ymdToDate = (ymd: string | null | undefined): Date | null => {
  const m = ymd ? /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(ymd) : null;
  if (!m || !isRealYmd(Number(m[1]), Number(m[2]), Number(m[3] ?? '1'))) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3] ?? '1'));
};

/**
 * NAT-2820 — an instant (a timestamp such as created_at) as the cell for its Europe/Rome calendar
 * day, whatever zone the browser is in: the Rome day, then the same local-midnight Date `ymdToDate`
 * gives, so the workbook shows the day Rome read.
 */
export const instantToRomeDate = (
  value: string | number | Date | null | undefined,
): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const instant = value instanceof Date ? value : new Date(value);
  return Number.isNaN(instant.getTime()) ? null : ymdToDate(todayInRome(instant));
};

const NUMERIC: readonly XlsxCellKind[] = [
  'integer',
  'decimal',
  'decimal2',
  'euro',
  'percent',
  'points',
];

/** What goes in the cell: null for anything unknown, so the cell is left empty. */
const cellValue = (value: XlsxValue, kind: XlsxCellKind): string | number | Date | null => {
  if (value === null || value === undefined) return null;
  if (NUMERIC.includes(kind)) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
  if (kind === 'date' || kind === 'month') {
    return value instanceof Date && !Number.isNaN(value.getTime()) ? value : null;
  }
  return String(value);
};

const sheetNameOf = (name: string, taken: Set<string>) => {
  const base = name.replace(/[[\]:*?/\\]/g, '_').substring(0, 31) || 'Foglio';
  let candidate = base;
  for (let n = 2; taken.has(candidate); n += 1) {
    const suffix = ` (${n})`;
    candidate = `${base.substring(0, 31 - suffix.length)}${suffix}`;
  }
  taken.add(candidate);
  return candidate;
};

const columnWidth = (column: XlsxColumn, rows: XlsxValue[][], index: number) => {
  const longest = rows.reduce((max, row) => {
    const v = row[index];
    if (v === null || v === undefined) return max;
    return Math.max(max, v instanceof Date ? 10 : String(v).length);
  }, column.header.length);
  return { wch: Math.min(Math.max(longest + 2, 10), 60) };
};

export function buildTypedWorkbook(spec: XlsxWorkbookSpec): WorkBook {
  const wb = utils.book_new();
  const taken = new Set<string>();
  spec.sheets.forEach((sheet) => {
    const top: (string | null)[][] = spec.preamble.map(([label, value]) => [label, value]);
    if (top.length) top.push([]);
    const headerRow = top.length;
    const body = sheet.rows.map((row) => sheet.columns.map((c, i) => cellValue(row[i], c.kind)));
    const ws = utils.aoa_to_sheet([...top, sheet.columns.map((c) => c.header), ...body]);
    body.forEach((_, r) => {
      sheet.columns.forEach((column, c) => {
        const format = XLSX_FORMATS[column.kind];
        const cell = ws[utils.encode_cell({ r: headerRow + 1 + r, c })];
        if (cell && format) cell.z = format;
      });
    });
    ws['!cols'] = sheet.columns.map((column, i) => columnWidth(column, sheet.rows, i));
    utils.book_append_sheet(wb, ws, sheetNameOf(sheet.name, taken));
  });
  return wb;
}

/** Builds the workbook and starts the download: `<fileName> dd-MM-yyyy.xlsx`. */
export function downloadTypedWorkbook(spec: XlsxWorkbookSpec) {
  const safe = spec.fileName.substring(0, 200).replace(/[/:?*[\]\\]/g, '_');
  // NAT-2481 review — compressed: a 50k×11 export goes from ~21.6 MB to ~4.9 MB uncompressed vs
  // compressed. SheetJS's default is uncompressed; this is the one flag that changes it.
  writeFileXLSX(buildTypedWorkbook(spec), `${safe} ${getDateForFileExport()}.xlsx`, {
    compression: true,
  });
}
